import { rollUpData } from 'src/utils/rollup';
import {
  mergeDataSets,
  budgetSnapshotsByCategories,
  splice,
  forecastCategories,
  calculateFullRangeData,
  weightedAverage,
} from './utils';
import PrepareForecast from './prepareForecast';

// Exporting the main function for forecasting expenses.
// This function integrates various data sources and calculations to provide a comprehensive view of expenses.
export default function useExpenseForecast(params) {
  const { aggregateData, ...forecastData } = expensesForecast(params);
  const spending = calculateFullRangeData(forecastData.segments, aggregateData, 'spending');
  const totalAverageSpending = weightedAverage(spending, params?.weights);

  return {
    ...forecastData,
    aggregateData,
    fullRangeSpending: spending,
    average: totalAverageSpending,
    ...(params?.budget && { budgets: params.budgets }),
  };
}

/**
 * Calculates and returns an expenses forecast based on provided data and parameters.
 *
 * @param {String} period - The period for which the forecast is to be made (e.g., 'monthly', 'yearly').
 * @param {Object} dateRange - The start and end dates defining the range of the forecast.
 * @param {Object} expenseData - The data related to expenses, including transactions and other relevant details.
 * @param {Array} patches - Modifications or adjustments to be applied to the budget.
 *
 * @return {Object} An object containing various details of the expenses forecast,
 *                  including budgets, data segments, aggregate data, historical series,
 *                  and forecasted series.
 */
function expensesForecast({
  period,
  dateRange,
  expenseData,
  patches,
  budgetSnapshots,
  annualInflationRate,
  fullRange,
  liabilities,
  weights,
}) {
  const expenseFilter = (cat) =>
    !cat?.startsWith('INCOME') &&
    !cat?.startsWith('TRANSFER') &&
    !['SOCIAL_SECURITY', 'BROKERAGE_WITHDRAWAL'].includes(cat);

  const { txByCategories, segments, calcSegments, calcPeriod, budgets } = PrepareForecast({
    period,
    dateRange,
    expenseData,
    patches,
    invertValue: false,
    fullRange,
    weights,
    options: { filter: expenseFilter },
  });

  const detailedForecast = forecastCategories(
    txByCategories,
    calcSegments,
    budgets,
    calcPeriod,
    annualInflationRate,
    weights
  );

  const mergedData = mergeDataSets(detailedForecast, detailedForecast, calcSegments, 0);

  // Create a higher-order function to apply the updateMortgagePayments function
  const applyLiabilityPaymentsToDataset = (dataset) =>
    updateLiabilityPayments(dataset, segments, liabilities);

  // Define the datasets you want to apply the function to
  const datasetsToApply = [
    rollUpData(calcSegments, segments, mergedData, period),
    rollUpData(calcSegments, segments, detailedForecast, period),
    rollUpData(calcSegments, segments, txByCategories, period),
  ];

  // Apply the higher-order function to all datasets using map
  const [aggregateData, forecastedSeries, historicalSeries] = datasetsToApply.map(
    applyLiabilityPaymentsToDataset
  );

  const options = { patches: budgets, filter: expenseFilter };
  const budgetedSeries = budgetSnapshotsByCategories(period, budgetSnapshots, segments, options);

  const datasets = {
    historic: historicalSeries,
    budgeted: budgetedSeries,
    forecast: forecastedSeries,
    fullPatch: detailedForecast,
    splice,
  };

  return {
    budgets,
    segments,
    aggregateData,
    budgetedSeries,
    historicalSeries,
    forecastedSeries,
    datasets,
  };
}

// New function to update mortgage payments
function updateLiabilityPayments(detailedForecast, segments, liabilities) {
  const results = { ...detailedForecast };

  const mortgageExpirationIndex = findMortgageExpirationIndex(segments, liabilities);
  if (mortgageExpirationIndex >= 0) {
    const forecastedMortgageSeries = forecastLiabilityPayments(
      detailedForecast?.LOAN_PAYMENTS_MORTGAGE_PAYMENT?.data,
      mortgageExpirationIndex
    );

    results.LOAN_PAYMENTS_MORTGAGE_PAYMENT = { data: forecastedMortgageSeries };
  }

  const studentExpirationIndex = findStudentExpirationIndex(segments, liabilities);
  if (studentExpirationIndex >= 0) {
    const forecastedStudentSeries = forecastLiabilityPayments(
      detailedForecast?.LOAN_PAYMENTS_STUDENT_LOAN_PAYMENT?.data,
      studentExpirationIndex
    );
    results.LOAN_PAYMENTS_STUDENT_LOAN_PAYMENT = { data: forecastedStudentSeries };
  }

  return results;
}

// Helper function to find mortgage expiration index
function findMortgageExpirationIndex(segments, liabilities) {
  const mortgageDebt =
    liabilities?.rows?.find((l) => l.detailedCategory?.includes('Mortgage'))?.data ?? [];

  return segments.findIndex((seg) => {
    const value = mortgageDebt[seg?.label];
    return value === 0;
  });
}

// Helper function to find student loan index
function findStudentExpirationIndex(segments, liabilities) {
  const mortgageDebt =
    liabilities?.rows?.find((l) => l.detailedCategory?.includes('Student'))?.data ?? [];

  return segments.findIndex((seg) => {
    const value = mortgageDebt[seg?.label];
    return value === 0;
  });
}

// Helper function to forecast mortgage payments
function forecastLiabilityPayments(mortgageData, mortgageExpirationIndex) {
  return Object.fromEntries(
    Object.entries(mortgageData ?? {}).map(([key, value], index) => {
      const newValue = index >= mortgageExpirationIndex ? 0 : value;
      return [key, newValue];
    })
  );
}
